import {
  FixedScreenHeaderStack,
  Heading,
  SearchAndAddBar,
} from '~/common/components/defaultUIComponents';
import EinmalAuftragApi from '~/common/api/zeiterfassungServerApi/EinmalAuftragApi';
import { EinmalAuftrag } from '~/common/entities/EinmalAuftraege/EinmalAuftragTypes';
import {
  Badge,
  Button,
  Checkbox,
  Flex,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { ListView } from '~/common/components/ListView';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function parseDurationDisplay(duration: string): string {
  if (!duration) return '';
  const parts = duration.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0] ?? '0', 10);
    const minutes = parseInt(parts[1] ?? '0', 10);
    return `${hours}h ${minutes}m`;
  }
  return duration;
}

const SoftDeleteDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ isOpen, onClose, onConfirm }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const translate = useTranslate();
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {translate('einmalAuftraege.softDelete.button')}
          </AlertDialogHeader>
          <AlertDialogBody>
            {translate('einmalAuftraege.softDelete.confirm')}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose}>
              {translate('einmalAuftraege.form.cancel')}
            </Button>
            <Button colorScheme="red" onClick={onConfirm} ml={3}>
              {translate('einmalAuftraege.softDelete.button')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export const EinmalauftraegeList: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [pendingSoftDelete, setPendingSoftDelete] =
    useState<EinmalAuftrag | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: einmalAuftraege = [], isLoading } =
    EinmalAuftragApi.hooks.useEinmalAuftraege();
  const { mutateAsync: softDelete } =
    EinmalAuftragApi.hooks.useSoftDeleteEinmalAuftrag();

  const toast = useToast({ status: 'error', description: 'generic.error' });

  const filtered = showDeleted
    ? einmalAuftraege
    : einmalAuftraege.filter((ea) => !ea.deleted);

  const handleSoftDeleteClick = (ea: EinmalAuftrag) => {
    setPendingSoftDelete(ea);
    onOpen();
  };

  const handleSoftDeleteConfirm = () => {
    if (!pendingSoftDelete) return;
    softDelete({ id: pendingSoftDelete.id })
      .catch(() => toast({}))
      .finally(() => {
        onClose();
        setPendingSoftDelete(null);
      });
  };

  return (
    <>
      <FixedScreenHeaderStack>
        <Heading>einmalAuftraege.heading</Heading>
        <SearchAndAddBar
          onInputChange={setSearchTerm}
          onClick={() => navigate(routes.CREATE_EINMAL_AUFTRAG)}
          inputPlaceholder="einmalAuftraege.heading"
          tooltipLabel="einmalAuftraege.create"
        />
      </FixedScreenHeaderStack>

      <HStack mt={3} mb={3}>
        <Checkbox
          isChecked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}
        >
          <Text>{translate('einmalAuftraege.showDeleted')}</Text>
        </Checkbox>
      </HStack>

      {isLoading ? null : (
        <ListView
          data={filtered}
          searchFields={(ea) => [ea.name, ea.description]}
          search={searchTerm}
          mt={3}
          render={({ data }) => (
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>{translate('einmalAuftraege.name.label')}</Th>
                  <Th>{translate('einmalAuftraege.description.label')}</Th>
                  <Th>{translate('einmalAuftraege.priority.label')}</Th>
                  <Th>{translate('einmalAuftraege.duration.label')}</Th>
                  <Th>{translate('einmalAuftraege.deleted.label')}</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {data.map((ea) => (
                  <Tr key={ea.id} opacity={ea.deleted ? 0.5 : 1}>
                    <Td>{ea.name}</Td>
                    <Td maxW="300px" isTruncated>
                      {ea.description}
                    </Td>
                    <Td>{ea.priority}</Td>
                    <Td>{parseDurationDisplay(ea.duration)}</Td>
                    <Td>
                      {ea.deleted ? (
                        <Badge colorScheme="red">
                          {translate('einmalAuftraege.deleted.label')}
                        </Badge>
                      ) : null}
                    </Td>
                    <Td>
                      <Flex gap={2}>
                        <Button
                          size="sm"
                          onClick={() =>
                            navigate(
                              routes.EDIT_EINMAL_AUFTRAG.replace(
                                ':id',
                                String(ea.id),
                              ),
                            )
                          }
                        >
                          ✏️
                        </Button>
                        {!ea.deleted && (
                          <Button
                            size="sm"
                            colorScheme="orange"
                            onClick={() => handleSoftDeleteClick(ea)}
                          >
                            {translate('einmalAuftraege.softDelete.button')}
                          </Button>
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        />
      )}

      <SoftDeleteDialog
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setPendingSoftDelete(null);
        }}
        onConfirm={handleSoftDeleteConfirm}
      />
    </>
  );
};
